import React from 'react';
import { cn } from '../../utils/cn';
import type { Form } from '../../interfaces/form.interface';
import { environment } from '../../config/environment';

interface LoaderProps {
  loaderImage?: string;
  configuration?: Form;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  id: string;
}

export const Loader: React.FC<LoaderProps> = ({ 
  loaderImage, 
  configuration,
  className,
  size = 'md',
  id 
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };

  // Usar loaderImage del prop, de la configuración, o favicon por defecto
  const imageToUse = loaderImage || 
    configuration?.theme?.visual?.loader?.image || 
    configuration?.theme?.visual?.loader?.favicon || 
    `${environment.API}/wallets/templates/${id}/images/loading`;

  if (imageToUse) {
    // Loader con imagen personalizada o favicon
    return (
      <div className={cn('flex flex-col items-center justify-center space-y-4', className)}>
        <div className={cn('relative', sizeClasses[size])}>
          <img
            src={imageToUse}
            alt="Loading..."
            className={cn(
              'w-full h-full object-contain animate-pulse',
              sizeClasses[size]
            )}
            onError={(e) => {
              // Si la imagen falla, usar el loader genérico
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.parentElement?.classList.add('fallback-loader');
            }}
          />
          <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
        </div>
      </div>
    );
  }

  // Loader genérico mejorado con parpadeo suave y lento
  return (
    <div className={cn('flex flex-col items-center justify-center space-y-4', className)}>
      <div className={cn('relative', sizeClasses[size])}>
        {/* Círculo exterior con parpadeo suave */}
        <div className={cn(
          'absolute inset-0 border-4 border-gray-200 rounded-full animate-pulse',
          sizeClasses[size]
        )} style={{ animationDuration: '2s' }}></div>
        
        {/* Círculo animado con rotación lenta */}
        <div className={cn(
          'absolute inset-0 border-4 border-transparent border-t-blue-600 rounded-full animate-spin',
          sizeClasses[size]
        )} style={{ animationDuration: '3s' }}></div>
        
        {/* Círculo interno con parpadeo alternado */}
        <div className={cn(
          'absolute inset-2 border-2 border-gray-300 rounded-full animate-pulse',
          sizeClasses[size]
        )} style={{ animationDuration: '2.5s', animationDelay: '0.5s' }}></div>
      </div>
      
      {/* Puntos animados con parpadeo lento */}
      <div className="flex space-x-2">
        <div 
          className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"
          style={{ animationDuration: '2s' }}
        ></div>
        <div 
          className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"
          style={{ animationDuration: '2s', animationDelay: '0.7s' }}
        ></div>
        <div 
          className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"
          style={{ animationDuration: '2s', animationDelay: '1.4s' }}
        ></div>
      </div>
    </div>
  );
}; 